import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type JSX,
} from "react";
import Loader from "./Loader";

type LottieLiteProps = {
  animationData: any;
  style?: CSSProperties;
  /** Playback speed multiplier. 1 = normal, 2 = twice as fast, 0.5 = half speed */
  speed?: number;
  /** Whether the animation should autoplay when loaded (default true) */
  autoplay?: boolean;
  /** Whether the animation should loop (default true) */
  loop?: boolean | number;
};

const LottieLite = ({
  animationData,
  style,
  speed = 1,
  autoplay = true,
  loop = true,
}: LottieLiteProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const animRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let canceled = false;

    import("lottie-web/build/player/lottie_light.min.js")
      .then((mod) => {
        if (canceled) return;
        const lottie = mod.default || mod;
        // Load without autoplay so we can set speed before playback begins.
        animRef.current = lottie.loadAnimation({
          container: containerRef.current,
          renderer: "svg",
          loop,
          autoplay: false,
          animationData,
        });

        // Apply requested speed immediately and then start playback if parent wanted autoplay.
        if (typeof animRef.current?.setSpeed === "function") {
          try {
            animRef.current.setSpeed(speed);
          } catch {
            // ignore
          }
        }

        if (autoplay && typeof animRef.current?.play === "function") {
          animRef.current.play();
        }
      })
      .catch((err) => {
        // If lottie fails to load, fail gracefully
        // eslint-disable-next-line no-console
        console.warn("Lottie failed to load:", err);
      })
      .finally(() => {
        if (!canceled) setLoading(false);
      });

    return () => {
      canceled = true;
      if (animRef.current && typeof animRef.current.destroy === "function") {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, [animationData, autoplay, loop, speed]);

  // If parent updates speed after load, apply it to the running animation
  useEffect(() => {
    if (animRef.current && typeof animRef.current.setSpeed === "function") {
      animRef.current.setSpeed(speed);
    }
  }, [speed]);

  return (
    <div
      style={{
        display: "inline-block",
        position: "relative",
        minWidth: 40,
        minHeight: 40,
      }}
    >
      <div ref={containerRef} style={style} />

      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
          }}
        >
          <Loader />
        </div>
      )}
    </div>
  );
};

export default LottieLite;
